import React from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { LazyMotion, MotionConfig, domAnimation } from "framer-motion";
import { ThemeProps } from "@heroui/use-theme";

import App from "./App.tsx";
import { Provider } from "./provider";
import { AuthProvider } from "./state/AuthContext";
import { SsrContextProvider } from "./ssr/ssrContext";
import { preloadRouteByPath } from "./utils/routePrefetch";
import "@/styles/globals.css";

const SSR_ROUTE_TYPES = new Set([
  "none",
  "blogPost",
  "pressReleasePost",
  "courseSingle",
  "courseCitySubject",
  "courseTutor",
]);

const getErrorMessage = (value: unknown) => {
  if (typeof value === "string") return value;
  if (value instanceof Error) return value.message || String(value);
  if (value && typeof value === "object") {
    const maybeMessage = (value as { message?: unknown }).message;

    if (typeof maybeMessage === "string") return maybeMessage;
  }

  return String(value ?? "");
};

if (typeof window !== "undefined") {
  const storedTheme = window.localStorage.getItem(ThemeProps.KEY);
  const rawTheme = storedTheme ?? ThemeProps.DARK;

  if (!storedTheme) {
    window.localStorage.setItem(ThemeProps.KEY, ThemeProps.DARK);
  }

  const resolvedTheme =
    rawTheme === ThemeProps.SYSTEM
      ? window.matchMedia?.("(prefers-color-scheme: dark)").matches
        ? ThemeProps.DARK
        : ThemeProps.LIGHT
      : rawTheme;

  document.documentElement.classList.remove(
    ThemeProps.LIGHT,
    ThemeProps.DARK,
    ThemeProps.SYSTEM,
  );
  document.documentElement.classList.add(resolvedTheme);
  document.documentElement.style.colorScheme =
    resolvedTheme === ThemeProps.DARK ? ThemeProps.DARK : ThemeProps.LIGHT;
}

const routeData = (() => {
  if (window.__SSR_ROUTE_DATA__) return window.__SSR_ROUTE_DATA__;
  const payloadElement = document.getElementById("ssr-route-data");
  const rawPayload = payloadElement?.textContent?.trim();

  if (!rawPayload) return undefined;
  try {
    const parsed = JSON.parse(rawPayload);
    const routeType = (parsed as { routeType?: unknown }).routeType;

    if (!SSR_ROUTE_TYPES.has(String(routeType))) {
      return undefined;
    }
    window.__SSR_ROUTE_DATA__ = parsed;

    return parsed;
  } catch {
    return undefined;
  }
})();

const rootElement = document.getElementById("root");

if (!rootElement) {
  throw new Error("Root element not found.");
}

const appTree = (
  <React.StrictMode>
    <BrowserRouter>
      <HelmetProvider>
        <SsrContextProvider routeData={routeData}>
          <AuthProvider>
            <Provider>
              {/* LazyMotion + `m` (in eager components) ship only the
                  `domAnimation` feature set on the critical path instead of the
                  full `motion` bundle. Not strict: lazy routes still using
                  `motion` load full features in their own off-critical chunks. */}
              <LazyMotion features={domAnimation}>
                <MotionConfig reducedMotion="user">
                  <App />
                </MotionConfig>
              </LazyMotion>
            </Provider>
          </AuthProvider>
        </SsrContextProvider>
      </HelmetProvider>
    </BrowserRouter>
  </React.StrictMode>
);

let hasFallenBackToClientRender = false;
let hydratedRoot: ReturnType<typeof ReactDOM.hydrateRoot> | null = null;

const hydrate = () => {
  hydratedRoot = ReactDOM.hydrateRoot(rootElement, appTree, {
    onRecoverableError: (error) => {
      const message = getErrorMessage(error);
      const shouldFallback =
        !hasFallenBackToClientRender &&
        /Suspense boundary|hydration|server rendering|#419/i.test(message);

      if (!shouldFallback) {
        return;
      }

      hasFallenBackToClientRender = true;
      window.queueMicrotask(() => {
        hydratedRoot?.unmount();
        ReactDOM.createRoot(rootElement).render(appTree);
      });
    },
  });
};

// The server now renders real content for lazy routes. Load the current
// route's chunk BEFORE hydrating so the lazy component resolves synchronously
// and hydrates cleanly against that markup (no fallback flash / mismatch).
const currentPath = `${window.location.pathname}${window.location.search}`;

Promise.resolve(
  preloadRouteByPath(currentPath, {
    mode: "navigation",
    isAuthenticated: true,
  }),
)
  .then(hydrate)
  .catch(hydrate);
